import { Box, Stack } from '@mui/material'
import { authHeader, history, QuestionnaireItemType, QuestionnaireStatus } from '../../../_helpers';
import { useDispatch, useSelector } from 'react-redux';
import BasicQuestionnaireDetails from './BasicQuestionnaireDetails';
import { URLS } from '../../../apis/urls';
import { questionnaireDetailActions } from '../../../_actions/questionnaireDetail.actions';
import { deleteQuestionnaireAnswerAttachment, questionnaireSendEmail } from '../../../apis/questionnaireApis';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QuestionnaireFormView from '../../Questionnaire/QuestionnaireFormView/QuestionnaireFormView';
import PreviewAndComplete from './PreviewAndComplete';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';


const QuestionnaireDetail = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const location = useLocation();
  const { questionnaireStore: { questionnaireDetails: { questionnaireDetails, currantSectionIndex } } } = useSelector(state => state);
  const { user } = useSelector((state) => state.authentication);
  const [formData, setFormData] = useState({ sections: [] });
  const [formValues, setFormValues] = useState(undefined);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
 const [errors, setErrors] = useState(new Map());
  const isCurrentUserExecutor = questionnaireDetails?.executorLoginId === user.id;
  const isSurveyInProgress = questionnaireDetails?.status === QuestionnaireStatus.InProgress;
  const canEditQuestionnaire = isCurrentUserExecutor && isSurveyInProgress;
  const questionnaireId = questionnaireDetails?.id;

  useEffect(() => {
    if (location.state?.openComplete) {
      setOpenCompleteDialog(true);
      history.replace({ ...history.location, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    if (questionnaireDetails?.sections) {
      setFormData({ sections: questionnaireDetails?.sections });
    }
  }, [questionnaireDetails]);

  const section = useMemo(
    () => formData.sections[currantSectionIndex],
    [formData, currantSectionIndex]
  );

  const handleFiles = async (sectionIndex, question, files, type = "add") => {
    if (type === "add") {
      const formData = new FormData();

      for (var i = 0; i < files.length; i++) {
        formData.append("files", files[i]);
      }

      const url = `${URLS.ADD_QUESTIONNAIRES_ANSWER_ATTACHMENTS}${encodeURIComponent(questionnaireDetails.id)}?questionId=${encodeURIComponent(question.id)}`;
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: authHeader(),
          body: formData,
        })
        const data = await response.json();
        if (data.success) {
          dispatch(questionnaireDetailActions.fetchQuestionnaireDetails(questionnaireDetails.id))
        }
      }
      catch (error) {
        console.log({ error })
      }

    }
    else if (type === "remove") {
      try {
        const response = await deleteQuestionnaireAnswerAttachment({ questionnaireId: questionnaireDetails.id, attachmentId: files.fileId });
        if (response?.data?.success) {
          dispatch(questionnaireDetailActions.fetchQuestionnaireDetails(questionnaireDetails.id))
        }
      } catch (error) {
        console.log({ error })
      }

    }
  }
  const normalizeAnswer = (ans) => {
    if (typeof ans === "string") {
      return ans.trim();
    }
    if (Array.isArray(ans)) {
      const cleaned = ans.map(a =>
        typeof a === "string" ? a.trim() : a
      ).filter(a => a !== "");

      return cleaned;
    }
    return ans;
  };
  const emptyValues = [undefined, null];

  const validateSection = () => {
    const newErrors = new Map();
    let isValid = true;
    section.questions.forEach((q, i) => {
      const isSignature = q.type === QuestionnaireItemType.Signature;
      let isEmpty = false
      let ans
      if (isSignature) {
        const textFieldValue = formValues?.[q.id]?.value
        const signatureValue = formValues?.[q.id]?.content
        isEmpty = (Array.isArray(textFieldValue) ? textFieldValue.filter(a => !!a).length === 0 : !textFieldValue?.trim()) || !signatureValue
      } else {
        ans = formValues?.[q.id];
        ans = normalizeAnswer(ans);
        isEmpty = emptyValues.includes(ans) || (Array.isArray(ans) && ans.filter(a => !!a).length === 0);
      }
      if (q.required && isEmpty) {
        isValid = false;
        newErrors.set(`${currantSectionIndex}-${q.id}`, t('questionnaire.form.requiredText'));      }
    });

    if (isValid) {
      setErrors(new Map());
    } else {
      setErrors(newErrors);
    }

    return isValid;
  };



  useEffect(() => {
    if (!questionnaireDetails?.sections) return;

    const allValues = questionnaireDetails.sections.reduce((acc, sec) => {
      sec.questions.forEach(q => {
        if (q.type === QuestionnaireItemType.Signature) {
          acc[q.id] = { content: q.signature?.content || "", value: q.answers };
        } else {
          acc[q.id] = q.answers;
        }
      });
      return acc;
    }, {});

    setFormValues(allValues);
  }, [questionnaireDetails]);


  const validateAllSections = () => {
    const newErrors = new Map();
    let isValid = true;
    let firstInvalidSectionIndex = null;

    questionnaireDetails.sections.forEach((sec, secIndex) => {
      sec.questions.forEach((q, qIndex) => {
        const isSignature = q.type === QuestionnaireItemType.Signature;
        let isEmpty = false
        let ans
        if (isSignature) {
          const textFieldValue = formValues?.[q.id]?.value
          const signatureValue = formValues?.[q.id]?.content
          isEmpty = (Array.isArray(textFieldValue) ? textFieldValue.filter(a => !!a).length === 0 : !textFieldValue?.trim()) || !signatureValue
        } else {
          ans = formValues?.[q.id];
          ans = normalizeAnswer(ans);
          isEmpty = emptyValues.includes(ans) || (Array.isArray(ans) && ans.filter(a => !!a).length === 0);
        }

        if (q.required && isEmpty) {
          isValid = false;
          newErrors.set(`${secIndex}-${q.id}`, t('questionnaire.form.requiredText'));
          if (firstInvalidSectionIndex === null) {
            firstInvalidSectionIndex = secIndex;
          }
        }
      });
    });

    setErrors(newErrors);

    return {
      isValid,
      firstInvalidSectionIndex
    };
  };
  const handleSendQuestionnaireEmail = async (payload) => {
    await questionnaireSendEmail(questionnaireId, payload);
  };
  return (
    <>
      <Box px={1.5}>
        <BasicQuestionnaireDetails validateAllSections={validateAllSections} showPdfIcon />
        <Stack>
          <QuestionnaireFormView
            canEditQuestionnaire={!canEditQuestionnaire}
            validateSection={validateSection}
            handleFiles={handleFiles}
            section={section}
            formData={formData}
            setFormData={setFormData}
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
            setErrors={setErrors}
          />
        </Stack>
        <PreviewAndComplete
          open={openCompleteDialog}
          closeModalHandler={() => setOpenCompleteDialog(false)}
          onSend={handleSendQuestionnaireEmail }

        />
      </Box>
    </>
  );
};


export default QuestionnaireDetail
